import { IconPack } from 'runtime/registry/icons/types';
import { Options } from 'highcharts';
import { MenuItem } from 'constants/menuConfiguration';
import { IFormDescriptorItem, ModalChartTypes } from 'types/Analytics';
import { FilterListItem, QuickFilterConfig } from 'types/QuickFilters';
import { ChartPreviewContentFn } from 'components/modal/modalList/graphDetails/GraphDetails';
import { RenderContentFn } from 'components/modal/modalList/graphDetails/components/settingsForm/SettingsForm';

export type FilterOverride = {
  hidden?: boolean;
  patch?: Partial<QuickFilterConfig>;
  list?: {
    add?: FilterListItem[];
    remove?: string[];
  };
  add?: QuickFilterConfig;
};

export type QuickFiltersOverride = Record<
  string,
  Record<string, FilterOverride>
>;

export type ResponsiveLayout<T> = {
  desktop?: T;
  mobile?: T;
};

export type LayoutValue<T> =
  | T
  | {
    desktop?: T;
    mobile?: T;
  };

export interface RuntimeComponentConfig {
  component?:
  | React.ComponentType<object>
  | React.LazyExoticComponent<React.ComponentType<any>>;

  props?: Record<string, any>;

  layout?: LayoutValue<Record<string, string[]>>;
}

export type ProfileFieldsOverride = Record<string, boolean>;

export interface WlConfig {
  version: string;
  iconLibrary: IconPack;
  components: Record<string, RuntimeComponentConfig>;
  isInternalSignup?: boolean;
  name: string;
  isNewDesignEnabled?: boolean;
  isNewComponentsEnabled?: boolean;
  componentsVersion?: string;
  menuConfiguration?: MenuItem[];
  quickFiltersOverride?: QuickFiltersOverride;
  profileFieldsOverride?: Record<string, ProfileFieldsOverride>;
  tableConfig?: {
    payments;
    invoices;
    teamUsers;
    teamActivities;
    projectCallbacks;
    reports;
    balanceRealTime;
    paymentOperationReference;
  };
  chartStyleTransformer?: Options | null;
  chartOptionsTransformer?: (
    options: Highcharts.Options,
    chartType?: string
  ) => Highcharts.Options;
  chartSettingsFormConfig?: {
    [key: string]: IFormDescriptorItem[];
  };
  chartTypes?: ModalChartTypes;
  copyright?: string;
  showRouteLeavingGuardIcon?: boolean;
  settingsFormWhatToRender?: RenderContentFn;
  chartPreviewConfig?: ChartPreviewContentFn;
  isHideLinksOnPage?: boolean;
  isHideSocialLinks?: boolean;
  isHideBalanceFilters?: boolean;
  isHidePaymentPageEditorForWl?: boolean;
  isColumnFixDisabled?: boolean;
}

interface ActiveWlState {
  wlId: number | null;
  config: WlConfig | null;
}

const DEFAULT_CONFIG: WlConfig = {
  version: '0.0.0',
  name: 'default',
  iconLibrary: IconPack['default'],
  components: {},
};

class ActiveWlConfigStore {
  private state: ActiveWlState = {
    wlId: null,
    config: null,
  };

  async setActiveWlConfig(wlId: number): Promise<void> {
    try {
      const module = await import(`./config.wl${wlId}.ts`);
      this.state = { wlId, config: module.default };
    } catch (error) {
      this.state = { wlId, config: DEFAULT_CONFIG };
    }
  }

  getActiveWlConfig(): WlConfig | null {
    return this.state.config;
  }

  getActiveWlId(): number | null {
    return this.state.wlId;
  }

  isConfigLoaded(): boolean {
    return this.state.config !== null;
  }

  reset(): void {
    this.state = { wlId: null, config: null };
  }
}

export const activeWlConfigStore = new ActiveWlConfigStore();
